import { z } from "zod";
import { DEFAULT_REQUIRED_DOCS } from "./documents.js";

/**
 * هوية المستأجر وإعدادات مستنداته.
 *
 * كل شركة عميلة تُصدر مستنداتها بهويتها هي: شعارها وألوانها وبياناتها
 * القانونية. هذا هو الفرق بين نظام تستأجره وبين نظام يبدو ملكًا لك.
 */

/** الحد الأقصى لحجم الشعار المخزَّن كسلسلة بيانات. */
export const MAX_LOGO_BYTES = 300_000;

/**
 * غلافُ الخدمة أوسعُ من الشعار وأضيقُ من صورةٍ خام.
 *
 * الغلافُ يُعرض بعرض بطاقةٍ لا يتجاوز ثلاثمئة بكسل، فنصفُ ميغابايتٍ منه
 * إسرافٌ يُثقل كلَّ قراءةٍ للكتالوج. والحدُّ يُفحص في المتصفّح قبل الرفع:
 * رفضُ الخادم بعد رفع ملفٍ كبير يهدر وقتَ المستخدم ويترك السببَ غامضًا.
 */
export const MAX_COVER_BYTES = 500_000;

const hexColor = z
  .string()
  .regex(/^#[0-9a-f]{6}$/i, "اللون يجب أن يكون بصيغة ست عشرية مثل ‎#3672EA.");

/**
 * الشعار يُخزَّن كسلسلة بيانات مضمّنة.
 *
 * البديل رفع الملفات وتخزينها، وهو ما يضيف خدمة تخزين ومسارات تحميل وصلاحيات
 * وصول. لشعار حجمه مئتا كيلوبايت لا يستحق ذلك، والتضمين يجعل المستند
 * المطبوع مكتفيًا بذاته بلا طلب شبكة.
 */
const logoDataUrl = z
  .string()
  .max(MAX_LOGO_BYTES, "حجم الشعار أكبر من الحد المسموح.")
  .refine(
    (v) =>
      v === "" || /^data:image\/(png|jpeg|jpg|svg\+xml|webp);base64,/.test(v),
    {
      message: "صيغة الشعار غير مدعومة. استخدم PNG أو JPG أو SVG.",
    },
  );

export const BrandingSchema = z.object({
  /** اللون الأساسي: الترويسة والعناوين في المستندات. */
  primary: hexColor.default("#3672EA"),
  accent: hexColor.default("#8CE553"),
  ink: hexColor.default("#12214A"),

  logoUrl: logoDataUrl.default(""),
  /** ختم أو توقيع يظهر أسفل المستند. */
  stampUrl: logoDataUrl.default(""),

  /** نص إضافي في ترويسة المستند، مثل شعار الشركة أو تصنيفها. */
  documentHeader: z.string().trim().max(300).default(""),
  /** نص التذييل: شروط عامة أو بيانات تواصل. */
  documentFooter: z.string().trim().max(600).default(""),

  /** إظهار الرقم الضريبي على المستندات — مطلوب للفواتير الرسمية. */
  showTaxNumber: z.boolean().default(true),
  showBankDetails: z.boolean().default(true),
});

export type Branding = z.infer<typeof BrandingSchema>;

export const DEFAULT_BRANDING_VALUES: Branding = BrandingSchema.parse({});

/* ==========================================================================
   أوراقُ المستندات — ليتر هيد A4 تُطبع البياناتُ فوقه
   ========================================================================== */

/**
 * ثلاثُ أوراق: العامّةُ (ليتر هيد) لكلّ مستندٍ مطبوع، وورقةٌ للفاتورة وأخرى
 * لكشف الحساب — وما لا ورقةَ خاصّةً له يُطبع على العامّة.
 */
export const DOCUMENT_PAPER_KINDS = ["letterhead", "invoice", "statement"] as const;
export type DocumentPaperKind = (typeof DOCUMENT_PAPER_KINDS)[number];

export const DOCUMENT_PAPER_LABELS: Record<DocumentPaperKind, string> = {
  letterhead: "ليتر هيد (A4)",
  invoice: "ورقة الفاتورة (A4)",
  statement: "ورقة كشف الحساب (A4)",
};

/**
 * حدُّ الورقة المخزَّنة: صورةٌ مضغوطةٌ (JPEG) بعرض A4 نحو 200dpi — تكفي
 * خلفيّةً مطبوعة. الواجهةُ تصغّر الملفّ المرفوع إليها قبل الحفظ، فلا يُرفض
 * ملفُّ 300dpi لحجمه.
 */
export const MAX_PAPER_BYTES = 2_000_000;

const paperDataUrl = z
  .string()
  .max(MAX_PAPER_BYTES, "حجم الورقة أكبر من الحد المسموح.")
  .refine((v) => v === "" || /^data:image\/(png|jpeg|jpg|webp);base64,/.test(v), {
    message: "صيغة الورقة غير مدعومة. استخدم PNG أو JPG.",
  });

/**
 * ورقةٌ وحدودُ الكتابة عليها بالمليمتر: البياناتُ تبدأ تحت ترويسة الورقة
 * وتنتهي فوق تذييلها، وتبتعد عن حافّتيها — في كلّ صفحةٍ من المستند.
 */
export const DocumentPaperSchema = z.object({
  imageUrl: paperDataUrl.default(""),
  top: z.number().min(0).max(150).default(45),
  bottom: z.number().min(0).max(150).default(30),
  side: z.number().min(0).max(60).default(15),
});
export type DocumentPaper = z.infer<typeof DocumentPaperSchema>;

/**
 * الأوراقُ في عمودٍ خاصٍّ بها لا في `branding`: صورٌ بمئات الكيلوبايتات لا
 * تُحمَّل مع كلّ قراءةٍ للهويّة (الإعدادات، صفحاتُ الحجز العامّة)، بل حين
 * يُطبع مستندٌ فقط — من `/settings/papers`.
 */
export const DocumentPapersSchema = z.object({
  letterhead: DocumentPaperSchema.default({}),
  invoice: DocumentPaperSchema.default({}),
  statement: DocumentPaperSchema.default({}),
});
export type DocumentPapers = z.infer<typeof DocumentPapersSchema>;
export const DEFAULT_DOCUMENT_PAPERS: DocumentPapers = DocumentPapersSchema.parse({});

/**
 * الورقةُ التي يُطبع عليها نوعُ مستند: ورقتُه الخاصّة إن رُفعت، وإلّا العامّة،
 * وإلّا لا ورقة — فيُطبع بترويسة النظام كما كان.
 */
export function documentPaperFor(
  papers: DocumentPapers | null | undefined,
  kind: DocumentPaperKind,
): DocumentPaper | null {
  if (!papers) return null;
  if (papers[kind]?.imageUrl) return papers[kind];
  if (papers.letterhead?.imageUrl) return papers.letterhead;
  return null;
}

/**
 * بيانات الشركة التي تظهر على المستندات.
 *
 * منفصلة عن الهوية البصرية لأنها تتغير لأسباب مختلفة: الألوان تتغير مع
 * تحديث الهوية، والعنوان يتغير مع الانتقال، والرقم الضريبي لا يتغير غالبًا.
 */
export const CompanyProfileSchema = z.object({
  legalName: z.string().trim().max(200).default(""),
  taxNumber: z.string().trim().max(40).default(""),
  commercialRegister: z.string().trim().max(40).default(""),
  phone: z.string().trim().max(40).default(""),
  email: z.string().trim().max(160).default(""),
  website: z.string().trim().max(200).default(""),
  address: z.string().trim().max(400).default(""),
  /**
   * الموقعُ على الخريطة — إحداثيّاتٌ «خطّ العرض، خطّ الطول» أو رابطُ خريطة.
   * يصلُ به الزائرُ إلى البابِ لا إلى الحيّ، ويُفتح بنقرةٍ في الخرائط.
   */
  gps: z.string().trim().max(300).default(""),

  /**
   * فروعُ الشركة.
   *
   * العنوانُ الواحدُ فوق يكفي شركةً في مكانٍ واحد. ومن له فروعٌ يحتاج لكلِّ
   * فرعٍ عنوانَه وهاتفَه وموقعَه — فتُحفظ قائمةً لا حقولًا مكرّرة.
   */
  branches: z
    .array(
      z.object({
        name: z.string().trim().max(120).default(""),
        address: z.string().trim().max(400).default(""),
        phone: z.string().trim().max(40).default(""),
        gps: z.string().trim().max(300).default(""),
      }),
    )
    .max(50)
    .default([]),

  /** بيانات التحويل البنكي على الفواتير. */
  bankName: z.string().trim().max(120).default(""),
  bankAccount: z.string().trim().max(60).default(""),
  bankIban: z.string().trim().max(60).default(""),
});

export type CompanyProfile = z.infer<typeof CompanyProfileSchema>;

export const DEFAULT_COMPANY_PROFILE: CompanyProfile =
  CompanyProfileSchema.parse({});

/** إعدادات تشغيلية يضبطها كل مستأجر. */
/**
 * إشعارات العميل بالبريد.
 *
 * تسعُ لحظاتٍ في دورة العمل يخرج عندها بريدٌ إلى العميل — حجزٌ تأكّد،
 * وعرضٌ أُرسل، وعقدٌ سرى، وفاتورةٌ صدرت، ودفعةٌ وصلت، ومشروعٌ بدأ.
 * وكانت كلُّها تخرج بلا سؤال. فصارت أربعَ مجموعاتٍ تُفتح وتُغلق: مجموعةً
 * أو حدثًا حدثًا — ولكلّ حدثٍ سطرٌ يُلحق برسالته، وللكلّ ذيلٌ واحد.
 *
 * الأحداث هنا هي ما يُرسَل فعلًا لا ما قد يُرسَل يومًا: حدثٌ في القائمة
 * له موضعٌ في الكود يطلقه.
 */
export const CLIENT_NOTIFICATION_GROUPS = [
  {
    key: "bookings",
    label: "الحجوزات",
    hint: "ما يخرج حين يحجز العميل من صفحة حجز.",
  },
  {
    key: "activities",
    label: "الأنشطة",
    hint: "ما يخرج حين يُحدَّد موعدٌ مع العميل.",
  },
  {
    key: "finance",
    label: "الأمور المالية",
    hint: "العروض والعقود والفواتير والسداد.",
  },
  {
    key: "projects",
    label: "إدارة المشروعات",
    hint: "بدء مشروع العميل وإقفاله.",
  },
] as const;
export type ClientNotificationGroup =
  (typeof CLIENT_NOTIFICATION_GROUPS)[number]["key"];

export const CLIENT_NOTIFICATION_EVENTS = [
  {
    key: "bookingConfirmed",
    group: "bookings",
    label: "تأكيد الحجز",
    hint: "الوقت والغرض والوسيلة والقيمة والمقدَّم والشروط — لحظة الحجز.",
  },
  {
    key: "meetingScheduled",
    group: "activities",
    label: "تحديد موعد اجتماع",
    hint: "الوقت والوسيلة ورابط الاجتماع أو مكانه — حين يُنشأ اجتماعٌ مع العميل.",
  },
  {
    key: "quotationSent",
    group: "finance",
    label: "إرسال عرض السعر",
    hint: "رقمه وقيمته وصلاحيته — حين يُعلَّم العرض مُرسَلًا.",
  },
  {
    key: "contractActivated",
    group: "finance",
    label: "سريان العقد",
    hint: "رقمه وقيمته ومدّته — حين يُفعَّل بعد توقيعه.",
  },
  {
    key: "invoiceSent",
    group: "finance",
    label: "إصدار الفاتورة",
    hint: "رقمها وإجماليها واستحقاقها — حين تُعلَّم مُرسَلة.",
  },
  {
    key: "recurringInvoice",
    group: "finance",
    label: "الفاتورة الدورية",
    hint: "فاتورة الشهر حين يُصدرها النظام تلقائيًّا.",
  },
  {
    key: "paymentReceipt",
    group: "finance",
    label: "إيصال السداد",
    hint: "ما سُدِّد وما بقي — حين تُؤكَّد الدفعة.",
  },
  {
    key: "projectStarted",
    group: "projects",
    label: "بدء المشروع",
    hint: "اسم المشروع ومن يقوده — حين يُنشأ مشروعٌ للعميل.",
  },
  {
    key: "projectClosed",
    group: "projects",
    label: "إقفال المشروع",
    hint: "حين يُطوى المشروع بعد تسليمه — موقوفٌ ابتداءً.",
  },
] as const;
export type ClientNotificationEvent =
  (typeof CLIENT_NOTIFICATION_EVENTS)[number]["key"];

const eventSetting = (enabled: boolean) =>
  z
    .object({
      enabled: z.boolean().default(enabled),
      /** سطرٌ يُلحق بالرسالة — «تواصل معنا على…» أو شرطٌ خاصّ. */
      note: z.string().trim().max(1000).default(""),
    })
    .default({});

export const ClientNotificationsSchema = z.object({
  /** المفتاح العامّ — مغلقًا لا يخرج بريدٌ إلى عميلٍ من أيّ حدث. */
  enabled: z.boolean().default(true),
  groups: z
    .object({
      bookings: z.boolean().default(true),
      activities: z.boolean().default(true),
      finance: z.boolean().default(true),
      projects: z.boolean().default(true),
    })
    .default({}),
  events: z
    .object({
      bookingConfirmed: eventSetting(true),
      meetingScheduled: eventSetting(true),
      quotationSent: eventSetting(true),
      contractActivated: eventSetting(true),
      invoiceSent: eventSetting(true),
      recurringInvoice: eventSetting(true),
      paymentReceipt: eventSetting(true),
      projectStarted: eventSetting(true),
      projectClosed: eventSetting(false),
    })
    .default({}),
  /** ذيلٌ يُختم به كلُّ بريدٍ للعميل — توقيعُ الشركة وأرقامُها. */
  footer: z.string().trim().max(1000).default(""),
});
export type ClientNotifications = z.infer<typeof ClientNotificationsSchema>;
export const DEFAULT_CLIENT_NOTIFICATIONS: ClientNotifications =
  ClientNotificationsSchema.parse({});

/** هل يخرج هذا الحدث؟ — سؤالٌ واحد تسأله كلُّ الخدمات قبل الإرسال. */
export function clientNotificationOn(
  cn: ClientNotifications | null | undefined,
  event: ClientNotificationEvent,
): boolean {
  const c = cn ?? DEFAULT_CLIENT_NOTIFICATIONS;
  const def = CLIENT_NOTIFICATION_EVENTS.find((e) => e.key === event);
  if (!def) return false;
  return c.enabled && c.groups[def.group] && c.events[event].enabled;
}

export const TenantSettingsSchema = z.object({
  /** نسبة الضريبة الافتراضية على المستندات الجديدة. */
  taxRate: z.number().min(0).max(100).default(14),
  /** مهلة سداد الفاتورة بالأيام. */
  invoiceDueDays: z.number().int().min(0).max(365).default(30),
  /** مدة صلاحية عرض السعر بالأيام. */
  quotationValidityDays: z.number().int().min(1).max(365).default(14),
  currency: z.string().trim().min(1).max(10).default("EGP"),
  currencyLabel: z.string().trim().min(1).max(10).default("ج.م"),

  /** شروط تُدرج تلقائيًا في عروض الأسعار الجديدة. */
  defaultQuotationTerms: z.string().trim().max(2000).default(""),
  defaultInvoiceTerms: z.string().trim().max(2000).default(""),
  defaultContractTerms: z.string().trim().max(4000).default(""),

  /*
   * الدوام والعطلات.
   *
   * مدخلات في حساب التأخير والإجازة والمرتب لا تفضيلات عرض: العطلة
   * الأسبوعية تحدد أي يوم يُخصم من رصيد الإجازة، وموعد بدء الدوام يحدد
   * متى يبدأ التأخير. تركها ثابتة في الكود يفرض عرف بلد على شركات أخرى.
   */
  workdayStart: z
    .string()
    .regex(/^\d{2}:\d{2}$/, "الوقت بصيغة ساعة:دقيقة.")
    .default("09:00"),
  workdayEnd: z
    .string()
    .regex(/^\d{2}:\d{2}$/, "الوقت بصيغة ساعة:دقيقة.")
    .default("17:00"),
  /** أيام العطلة الأسبوعية بترقيم getDay: الأحد صفر. */
  weekendDays: z.array(z.number().int().min(0).max(6)).max(7).default([5, 6]),
  /** دقائق تأخير مسموح بها قبل أن يُحتسب التأخير. */
  lateGraceMinutes: z.number().int().min(0).max(240).default(15),
  /*
   * التأمينات وضريبة الدخل.
   *
   * أرقامٌ يُدخلها المحاسبُ لا تُكتب في الشيفرة: القوانينُ تتغيّر بقرارٍ
   * في الجريدة الرسمية، ونظامٌ يحبس نسبةً في شيفرته يجعل تعديلَ قانونٍ
   * إصدارَ برنامج. والملاحظةُ تحتها ليكتب من أدخلها أيَّ قانونٍ أدخل.
   */
  insuranceEmployeeRate: z.number().min(0).max(100).default(11),
  insuranceEmployerRate: z.number().min(0).max(100).default(18.75),
  insuranceWageMin: z.number().min(0).max(1_000_000).default(2_000),
  insuranceWageMax: z.number().min(0).max(1_000_000).default(14_500),
  taxPersonalExemption: z.number().min(0).max(10_000_000).default(20_000),
  taxBrackets: z
    .array(
      z.object({
        /** حدُّ الشريحة السنويّ — فارغٌ للشريحة الأخيرة المفتوحة. */
        upTo: z.number().min(0).max(1_000_000_000).nullable(),
        rate: z.number().min(0).max(100),
      }),
    )
    .max(12)
    .default([
      { upTo: 40_000, rate: 0 },
      { upTo: 55_000, rate: 10 },
      { upTo: 70_000, rate: 15 },
      { upTo: 200_000, rate: 20 },
      { upTo: 400_000, rate: 22.5 },
      { upTo: 1_200_000, rate: 25 },
      { upTo: null, rate: 27.5 },
    ]),
  /** أيُّ قانونٍ أُدخل ومتى — يقرؤه من يأتي بعد. */
  taxRulesNote: z.string().trim().max(300).default(""),

  /** رصيد الإجازة السنوية للموظف الجديد. */
  annualLeaveDefault: z.number().min(0).max(365).default(21),

  /*
   * الاجتماعات.
   *
   * لا تكامل مع Zoom أو Meet: ذلك يحتاج موافقة تطبيق من كل مزوّد ومفاتيح
   * لكل شركة على حدة. الرابط الثابت — غرفة الاجتماع الدائمة — يؤدي الغرض
   * نفسه بلا تكامل: يُدرَج تلقائيًا في كل اجتماع من هذا النوع.
   */
  defaultMeetingProvider: z
    .enum(["ONSITE", "JITSI", "ZOOM", "MEET", "TEAMS", "PHONE"])
    .default("ONSITE"),
  /** غرفة الاجتماع الدائمة، إن كان للشركة واحدة. */
  meetingRoomUrl: z.string().trim().max(500).default(""),
  /** المدة الافتراضية للاجتماع بالدقائق. */
  defaultMeetingMinutes: z.number().int().min(5).max(480).default(30),
  /** التذكير قبل الموعد بالدقائق؛ صفر يعني بلا تذكير. */
  meetingReminderMinutes: z.number().int().min(0).max(1440).default(15),
  /**
   * الاجتماعاتُ الداخليّةُ عبر Jitsi — مفعّلةٌ افتراضيًّا، تعمل داخلَ النظام
   * دون أداةٍ خارجيّة. النطاقُ قابلٌ للتغييرِ إلى خادمِ Jitsi خاصٍّ لاحقًا.
   */
  jitsiEnabled: z.boolean().default(true),
  jitsiDomain: z.string().trim().max(200).default("meet.jit.si"),

  /**
   * كلمةُ الحماية تحرسُ أمرين لا أمرًا واحدًا: حذفَ المنجَز، وكشفَ المعلومات
   * الحسّاسة. هذا المفتاحُ يضبطُ الثاني — فمن أراد الحذفَ محروسًا والكشفَ
   * مفتوحًا أطفأه.
   */
  sensitiveRequiresPassword: z.boolean().default(true),

  /*
   * العمولات.
   *
   * هذه قواعد افتراضية تُنسخ في الدورة عند إنشائها لا تُقرأ منها عند العرض:
   * تعديل النسبة اليوم يجب ألّا يغيّر عمولة شهر أُقفل وصُرف.
   */
  commissionBasis: z.enum(["COLLECTED", "INVOICED"]).default("COLLECTED"),
  commissionTargetPolicy: z
    .enum(["NONE", "ABOVE_TARGET", "GATE"])
    .default("NONE"),
  /** النسبة الافتراضية لمن لم تُحدَّد له نسبة في ملفه. */
  commissionDefaultRate: z.number().min(0).max(100).default(0),

  /*
   * الأهداف البيعية.
   *
   * ثلاثةٌ لا واحد: السنويُّ يُقاس عليه اتجاهُ الشركة، والشهريُّ إيقاعُ
   * الفريق، والأسبوعيُّ نبضُه. وقسمةُ السنويّ على اثني عشر لا تُغني —
   * لا الشهورُ سواءٌ في البيع ولا الأسابيع، فيُكتب لكلٍّ رقمُه.
   *
   * وصفرٌ يعني «لا هدف»: فلا تُعرض نسبةُ إنجازٍ إلى صفر، ولا يُقال لمن
   * لم يضع هدفًا إنّه قصّر عنه.
   */
  salesTargetYear: z.number().min(0).max(99_999_999_999.99).default(0),
  salesTargetMonth: z.number().min(0).max(99_999_999_999.99).default(0),
  salesTargetWeek: z.number().min(0).max(99_999_999_999.99).default(0),

  /*
   * كشوف الحساب الشهرية.
   *
   * يُصدرها النظام آليًّا لكل عميل: صفر يعني آخر يوم من الشهر (كشفُ
   * الشهر يُقفل بانقضائه)، وما بين 1 و28 يومُ إصدارٍ ثابت في الشهر
   * التالي — فوق 28 يُمنع لأن فبراير لا يبلغه.
   */
  statementDay: z.number().int().min(0).max(28).default(0),

  /*
   * حد ائتمان العملاء.
   *
   * الإلزام معطّل ابتداءً عمدًا: تفعيله على شركة قائمة قد يوقف فواتير
   * لعملاء تجاوزوا سقوفًا لم تُضبط بعد، فيُترك للشركة أن تراجع أرصدتها
   * ثم تفعّله.
   */
  creditEnforcement: z.enum(["OFF", "WARN", "BLOCK"]).default("OFF"),
  /** سقف الشركة لمن لا سقف خاص له. صفر يعني ألّا سقف افتراضي. */
  creditLimitDefault: z.number().min(0).max(99_999_999_999.99).default(0),

  /*
   * رصيد الخصومات الشهري للمندوب.
   *
   * سقفٌ لما يمنحه المندوب من خصوماتٍ في عروضه خلال الشهر، يُحسب على العروض
   * المرسَلة والمقبولة. غرضه ألّا يتصرّف المندوب في هامش الربح بلا حد.
   *
   * الإلزام معطّل ابتداءً كحدّ الائتمان: تفعيله على فريقٍ قائم قد يمنع عروضًا
   * بُنيت على خصومٍ اعتادها، فيُترك للشركة أن تضبط الأرصدة ثم تفعّله. و«تنبيه»
   * يُظهر التجاوز ولا يمنعه، و«منع» يردّ الإرسال حتى يُخفَّض الخصم أو يرسله مدير.
   */
  discountEnforcement: z.enum(["OFF", "WARN", "BLOCK"]).default("OFF"),

  /*
   * توقيع الشركة على العقود.
   *
   * ختمٌ إلكترونيّ للشركة لا توقيعٌ موثَّق: صورةٌ تُحفظ مرةً وتُطبع على العقد
   * باسم موقّعها وتاريخه، فتوفّر خطوة الطبع والتوقيع اليدوي. ولا تُغني عن
   * توقيع العميل ولا تُثبت هويّته — ويُقال ذلك في الشاشة صراحةً بدل أن يُفهم
   * منه ما لا يفعله.
   */
  contractSignatureUrl: z.string().trim().max(200_000).default(""),
  contractSignerName: z.string().trim().max(120).default(""),
  /** توقيعٌ تلقائيّ عند تفعيل العقد، بلا خطوةٍ يدوية. */
  contractAutoSign: z.boolean().default(false),
  /**
   * بنود العقد الافتراضية، تُدرَج في كل عقدٍ جديد.
   *
   * تُنسخ في العقد لحظة إنشائه ولا تُقرأ منها عند العرض: تعديل الشروط اليوم
   * يجب ألّا يغيّر عقدًا وُقّع العام الماضي.
   */
  defaultContractClauses: z
    .array(
      z.object({
        title: z.string().trim().max(200),
        body: z.string().trim().max(8000),
      }),
    )
    .max(50)
    .default([]),
  /** رصيد الشركة الشهري لمن لا رصيد خاص له. صفر يعني ألّا رصيد افتراضي. */
  discountBudgetDefault: z.number().min(0).max(99_999_999_999.99).default(0),

  /*
   * مفتاح الدولة الافتراضي، بلا علامة زائد.
   *
   * به يُفهم الرقم المحلي: 01012345678 يصير +201012345678. وبلا مفتاح
   * يُحفظ الرقم كما كُتب ولا يُخمَّن بلده — التخمين يضع أرقامًا في بلد
   * غير بلدها ويجعل الاتصال يفشل بلا سبب ظاهر.
   */
  defaultCountryCode: z
    .string()
    .trim()
    .regex(/^\d{0,4}$/, "مفتاح الدولة أرقام فقط.")
    .default("20"),

  /** إشعارات العميل بالبريد — مجموعاتٌ وأحداثٌ تُفتح وتُغلق. */
  clientNotifications: ClientNotificationsSchema.default({}),

  /*
   * أوراقُ الموظّف التي تطلبها الشركة.
   *
   * إعدادُ شركةٍ لا ثابتٌ في الشيفرة: ما يلزم في مصرَ غيرُ ما يلزم في
   * الخليج، وفرضُ قائمةٍ واحدةٍ يجعل «ملفّاتٌ ناقصة» رقمًا لا يصف أحدًا.
   * ومفاتيحُها من كتالوج المستندات، ويُرشَّح ما لا يُعرف منها عند القراءة.
   */
  requiredEmployeeDocs: z
    .array(z.string().trim().max(40))
    .max(40)
    .default([...DEFAULT_REQUIRED_DOCS]),
});

export type TenantSettings = z.infer<typeof TenantSettingsSchema>;

export const DEFAULT_TENANT_SETTINGS: TenantSettings =
  TenantSettingsSchema.parse({});

/**
 * شروط مبدئية تُقترح على المستأجر الجديد.
 *
 * تُقترح لا تُفرض: الشروط التزام قانوني، وفرض نص جاهز يجعل الشركة تُصدر
 * مستندات بشروط لم تقرأها.
 */
export const SUGGESTED_TERMS = {
  quotation: [
    "هذا العرض ساري لمدة أربعة عشر يومًا من تاريخ إصداره.",
    "الأسعار المذكورة لا تشمل ضريبة القيمة المضافة ما لم يُذكر خلاف ذلك.",
    "يبدأ التنفيذ بعد سداد الدفعة المقدمة واعتماد المتطلبات كتابةً.",
    "أي تعديل خارج نطاق العرض يُسعَّر على حدة.",
  ],
  invoice: [
    "تُستحق الفاتورة خلال ثلاثين يومًا من تاريخ إصدارها.",
    "يُرجى ذكر رقم الفاتورة عند التحويل.",
    "التأخر في السداد قد يترتب عليه إيقاف الخدمة.",
  ],
  contract: [
    "مدة العقد كما هو محدد أعلاه، ويُجدَّد باتفاق كتابي من الطرفين.",
    "يلتزم الطرف الأول بتقديم الخدمات المتفق عليها وفق الجدول الزمني المرفق.",
    "يلتزم الطرف الثاني بسداد الدفعات في مواعيدها المحددة.",
    "لا يجوز لأي طرف التنازل عن العقد للغير دون موافقة كتابية.",
    "يخضع هذا العقد لأحكام القانون المصري، وتختص محاكم القاهرة بأي نزاع.",
  ],
} as const;
